import React from "react";
import { canonical } from "../data/business";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Check } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import QuoteForm from "../components/QuoteForm";
import FaqAccordion from "../components/FaqAccordion";
import { findService } from "../data/services";
import { findLocation } from "../data/locations";

export default function ServiceLocation() {
  const { service, location } = useParams();
  const s = findService(service);
  const l = findLocation(location);
  if (!s || !l) return <Navigate to="/" replace />;

  // Strip the trailing word "Installation" from the location heading so we don't
  // produce duplicates like "Artificial Grass Installation Installation Edinburgh".
  const shortName = s.name.replace(/\s+Installation$/i, "");
  const title = `${s.name} ${l.name}`;
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "Service", "name": title, "areaServed": l.name, "provider": { "@type": "LocalBusiness", "name": "Last Cut Landscaping", "@id": "https://lastcutlandscaping.co.uk/#business" }, "description": `${s.name} in ${l.name}, ${l.region}` },
    { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": s.faqs.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) },
  ];

  return (
    <div className="page-enter">
      <Seo
        title={`${shortName} ${l.name} — Last Cut Landscaping`}
        description={`Premium ${shortName.toLowerCase()} in ${l.name}, ${l.region}. ${s.short} Free quotes, 2-year guarantee, fully insured.`}
        canonical={canonical(`/${s.slug}/${l.slug}`)}
        ogImage={s.image}
        jsonLd={jsonLd}
      />

      <section className="bg-cream pt-8 pb-12 lg:pb-20 lg:pt-12">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Breadcrumbs items={[{ label: s.name, to: `/services/${s.slug}` }, { label: l.name }]} />
          <div className="grid lg:grid-cols-12 gap-10 mt-6 items-end">
            <div className="lg:col-span-7">
              <div className="pill mb-4"><MapPin size={12}/> {l.region}</div>
              <h1 className="text-display font-display text-[clamp(2.2rem,5vw,4rem)] text-dark">{s.name} <span className="italic text-[color:var(--lc-green)]">{l.name}</span></h1>
              <p className="text-lg text-dark/75 mt-5 max-w-2xl">Premium {s.name.toLowerCase()} for homes across {l.name} and {l.region}. {s.intro}</p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link to="/quote" className="btn-primary">Free {l.name} quote <ArrowRight size={16}/></Link>
                <Link to={`/services/${s.slug}`} className="btn-ghost">More on {s.name}</Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden aspect-[4/3]"><img src={s.image} alt={`${s.name} in ${l.name}, Scotland`} className="w-full h-full object-cover"/></div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 className="font-display text-3xl lg:text-4xl text-dark mb-6">Why {l.name} homeowners choose Last Cut</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {s.benefits.map((b, i) => <li key={i} className="flex gap-3 p-4 bg-paper rounded-xl"><Check className="text-[color:var(--lc-green)] mt-0.5" size={18}/><span className="text-sm text-dark/80">{b}</span></li>)}
            </ul>
            <div className="mt-8">
              <h3 className="font-display text-2xl text-dark mb-3">Local areas we cover</h3>
              <div className="flex flex-wrap gap-2">{l.neighborhoods.map((n) => <span key={n} className="px-3 py-1.5 bg-white rounded-full border border-lc text-sm text-dark/80">{n}</span>)}</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <QuoteForm defaultService={s.name} sourcePage={`/${s.slug}/${l.slug}`} />
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-paper">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <h2 className="font-display text-3xl lg:text-4xl text-dark text-center mb-8">{title} — common questions</h2>
          <FaqAccordion items={s.faqs} />
        </div>
      </section>
    </div>
  );
}
